// 1). The largest sum of point coordinates (X, Y) is in the Top Right Corner.

// 2). The Smallest sum of points is The Bottom Left Point.

// 3). The largest difference of points is the Top left Corner

// 4). The smallest difference of points is the Bottom Left Corner





import Webcam from "react-webcam";
import { AlertDialog, AlertDialogCancel, AlertDialogContent } from "../../../components/ui/alert-dialog"
import { ArrowRightIcon, ImageIcon, RefreshCcwIcon, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import lodash from "lodash"

type P = {
    open?: boolean
    onOpenChange?: (open:boolean) => void
    onSubmit?: (b64:string) => void
}

declare const cv: any;

const BarcodeScanner = ({open, onOpenChange, onSubmit}:P) => {
    // Initialize state to store captured image
    const [imageSrc, setImageSrc] = useState<string | null | undefined>(null);
    const [isEnvironmentFacing, setisEnvironmentFacing] = useState(true);

    const webcamRef = useRef<Webcam>(null);
    const webcamCanvasRef = useRef<HTMLCanvasElement>(null);

    const screenshotCanvasRef = useRef<HTMLCanvasElement>(null);

    const previousContours = useRef([]);
    const [documentScanningStatus, setDocumentScanningStatus] = useState<'none' | 'loading' | 'found' | null>(null);
    const detectionStartTime = useRef<number | null>(null);

    const isUploadedFile = useRef(false)
    const videoSize = useRef<{width:number,height:number}>()
    const imageRef = useRef<HTMLImageElement | null>(null);

    const [adjustedCorners, setAdjustedCorners] = useState<{x:number,y:number}[] | null>(null);

    const onChangeOpen = (open:boolean) => {
      if(open){
        console.log("reset")
        setImageSrc(null);
        previousContours.current = []
        setDocumentScanningStatus(null);
        detectionStartTime.current = null;
        videoSize.current = undefined;
        imageRef.current = null;
        setAdjustedCorners(null);
        isUploadedFile.current = false;
      }
      onOpenChange&&onOpenChange(open)
    }
    
    // Function to handle capturing image
    const capture = useCallback(() => {
        if(!webcamRef.current){ return; }
        const imageSrc = webcamRef.current.getScreenshot();
        setImageSrc(imageSrc);
    }, [webcamRef]);

    useEffect(()=>{
        if(imageSrc){
            if(screenshotCanvasRef.current){ 
              console.log(adjustedCorners)
              // Already found corners
              if(adjustedCorners){ drawAdjustables() }

              // Not found corners
              if(!adjustedCorners){ 
                console.log("no adjusted corners found")
                setCornersImage()
              }
            }
        }  
    }, [imageSrc])

    const drawAdjustables = () => {
        const canvas = screenshotCanvasRef.current

        if(!canvas) return;

        const ctx = canvas.getContext("2d", { willReadFrequently: true });
        if (!ctx) return;

        canvas.width = videoSize.current?.width || window.innerWidth
        canvas.height = videoSize.current?.height || imageRef.current?.height || window.innerHeight

        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if(!adjustedCorners) return;

        drawCornerCircles(ctx, adjustedCorners);
        drawFullContour(ctx, adjustedCorners);
    }

    const handleUserMediaError = (error:any) => {
        console.error("User media error:", error);
    };

    const processFrame = () => {
      if (!webcamRef.current || !webcamCanvasRef.current) return;
  
      const video = webcamRef.current.video;
      const canvas = webcamCanvasRef.current;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      
      if(!video || !canvas || !ctx) return
      if (video.readyState !== 4) return;
  
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
  
      // Draw video feed onto canvas
      ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
      const frame = ctx.getImageData(0, 0, video.videoWidth, video.videoHeight);
  
      const src = cv.matFromImageData(frame);
      const gray = new cv.Mat();
      cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
  
      const blurred = new cv.Mat();
      cv.GaussianBlur(gray, blurred, new cv.Size(5, 5), 1);
  
      const edges = new cv.Mat();
      cv.Canny(blurred, edges, 75, 200);
  
      const contours = new cv.MatVector();
      const hierarchy = new cv.Mat();
      cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
  
      const imgArea = video.videoWidth * video.videoHeight;
      let largestContour = null;
      let largestArea = 0;
  
      for (let i = 0; i < contours.size(); i++) {
          const contour = contours.get(i);
          const area = cv.contourArea(contour);
  
          if (area < imgArea * 0.1) continue; // Filter out small contours
  
          const perimeter = cv.arcLength(contour, true);
          const approx = new cv.Mat();
          cv.approxPolyDP(contour, approx, 0.02 * perimeter, true);
  
          if (approx.rows === 4) {
              const points = [];
              for (let j = 0; j < 4; j++) {
                  points.push({
                      x: approx.data32S[j * 2],
                      y: approx.data32S[j * 2 + 1],
                  });
              }
  
              // Calculate aspect ratio
              const width = Math.max(
                  distanceFunc(points[0], points[1]),
                  distanceFunc(points[2], points[3])
              );
              const height = Math.max(
                  distanceFunc(points[0], points[3]),
                  distanceFunc(points[1], points[2])
              );
              const aspectRatio = width / height;
              if (aspectRatio < 0.5 || aspectRatio > 2.0) {
                  approx.delete();
                  continue; // Filter out contours that do not resemble a document
              }
  
              if (area > largestArea) {
                  largestArea = area;
                  largestContour = points;
              }
          }
          approx.delete();
      }
  
      if (largestContour) {
          setDocumentScanningStatus('loading');

          const smoothedContour = smoothContours(previousContours.current, largestContour);
          drawCornerCircles(ctx, smoothedContour);
          previousContours.current = smoothedContour;
  
          const currentTime = new Date().getTime();
          if (!detectionStartTime.current) {
            detectionStartTime.current = currentTime;
          }
  
          if (currentTime - detectionStartTime.current > 2000) {
              setDocumentScanningStatus('found');
              videoSize.current = {width:canvas.width, height:canvas.height}
              setAdjustedCorners([...smoothedContour]);
              previousContours.current  = []
              
              capture();
          }
      }
      else {

        if(previousContours.current){
          drawCornerCircles(ctx, previousContours.current);
        }
        
        const currentTime = new Date().getTime();
        if (detectionStartTime.current && currentTime - detectionStartTime.current > 2000){ 
          setDocumentScanningStatus('none'); 
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          previousContours.current = []
          detectionStartTime.current = null;
        }
      }
  
      // Release memory
      src.delete();
      gray.delete();
      blurred.delete();
      contours.delete();
      hierarchy.delete();
    };

    const tick = () => {
        processFrame();
        requestAnimationFrame(tick);
    };
    
    useEffect(() => {
      if(open){
        requestAnimationFrame(tick);
        return () => {
            //@ts-ignore
            cancelAnimationFrame(tick);
        };
      } else {
        //@ts-ignore
        cancelAnimationFrame(tick);
      }
    }, [open]);

    const handleMouseDown = (e:React.MouseEvent<HTMLCanvasElement, MouseEvent> | React.TouchEvent<HTMLCanvasElement>) => {
        if((!Array.isArray(adjustedCorners)) || !screenshotCanvasRef.current) return;
        //if(!videoSize.current){ videoSize.current = {width:screenshotCanvasRef.current.width, height:screenshotCanvasRef.current.height}}

        const canvas = screenshotCanvasRef.current;
        const rect = canvas.getBoundingClientRect();

        let clientX, clientY;

        if ("touches" in e) {
            // For touch events
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            // For mouse events
            clientX = e.clientX;
            clientY = e.clientY;

            //if(!videoSize.current){ videoSize.current = {width:screenshotCanvasRef.current.width, height:screenshotCanvasRef.current.height}}
        }

        let mX = clientX;
        let mY = clientY;

        mX = (!isUploadedFile.current && videoSize.current) ? ((videoSize.current.width || 1) * (clientX / rect.width)) : clientX
        mY = (!isUploadedFile.current && videoSize.current) ? ((videoSize.current.height || 1) * (clientY / rect.height)) : clientY

        let closestCornerIndex = -1;
        let minDistance = 30;

        adjustedCorners.forEach((corner:any, index:number) => {
            const distance = distanceFunc({ x: mX, y: mY }, corner);
            if (distance < minDistance) {
              minDistance = distance;
              closestCornerIndex = index;
            }
        });

        if(closestCornerIndex !== -1) {
          const handleMouseMove = (moveEvent:any) => {
              //if(!videoSize.current) return;

              if (moveEvent.type === "touchmove") {
                  moveEvent = moveEvent.touches[0];
              }

              let newMouseX = moveEvent.clientX;
              let newMouseY = moveEvent.clientY;

              

              newMouseX = (!isUploadedFile.current && videoSize.current) ? (rect.width / moveEvent.clientX, videoSize.current.width / (rect.width / moveEvent.clientX)) : newMouseX;
              newMouseY = (!isUploadedFile.current && videoSize.current) ? (rect.height / moveEvent.clientY, videoSize.current.height / (rect.height / moveEvent.clientY)) : newMouseY;

              let tmpAdjustedCorners = [...adjustedCorners]
              tmpAdjustedCorners[closestCornerIndex] = { x:newMouseX , y:newMouseY}

              const cvs = screenshotCanvasRef.current
              if(!cvs) return;
              const ctx = cvs?.getContext("2d");
              ctx?.clearRect(0, 0, cvs.width, cvs.height);
              drawCornerCircles(ctx, tmpAdjustedCorners);
              drawFullContour(ctx, tmpAdjustedCorners)

              setAdjustedCorners(tmpAdjustedCorners)
          };
      
          const handleMouseUp = () => {
              document.removeEventListener("mousemove", handleMouseMove);
              document.removeEventListener("mouseup", handleMouseUp);

              document.removeEventListener("touchmove", handleMouseMove);
              document.removeEventListener("touchend", handleMouseUp);
          };
      
          document.addEventListener("touchmove", handleMouseMove)
          document.addEventListener("touchend", handleMouseUp)

          document.addEventListener("mousemove", handleMouseMove);
          document.addEventListener("mouseup", handleMouseUp);
        }
    }

    const getImageDimensionsFromBase64 = (base64String:string) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
      
          img.onload = () => {
            resolve({
              width: img.width,
              height: img.height
            });
          };
      
          img.onerror = (error:any) => {
            reject("Error loading image: " + error.message);
          };
      
          img.src = `data:image/jpeg;base64,${base64String}`;
        });
    };

    async function setCornersImage () {
      toast.loading('Detecting corners...', { id:'setCornersImage' })
      const canvas = screenshotCanvasRef.current;
      
      const base64 = imageSrc?.split('base64,')[1];
      if(!canvas || !base64){ toast.remove('setCornersImage'); return; }

      canvas.width = imageRef.current?.width || 0
      canvas.height = imageRef.current?.height || 0
      
      const ctx = canvas.getContext("2d");
      if(!ctx){ toast.remove('setCornersImage'); return;}
      const rect = canvas.getBoundingClientRect();

      try {
        const corners = await detectDocumentCornersFromBase64(base64) as {x:number,y:number}[]
        
        const resolutionImage = await getImageDimensionsFromBase64(base64) as {width:number,height:number}

        corners.forEach(point => {
            point.x = Math.round((rect.width / resolutionImage.width) * point.x)
            point.y = Math.round((rect.height / resolutionImage.height) * point.y)
        });

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        drawCornerCircles(ctx, corners)
        drawFullContour(ctx, corners)
        setAdjustedCorners(corners);
        toast.remove('setCornersImage');
      } catch (e) { 
        console.log(e);
        toast.error('Detecting corners failed', { id:'setCornersImage' })
        // setImageSrc(null);
      }
    }

    const handleUploadFile = (e:React.ChangeEvent<HTMLInputElement>) => {
        if(!e.target || !e.target.files) return;
        const file = e.target.files[0]; // Get the first file selected

        if (file) {
          const reader = new FileReader();
      
          // Define the callback to execute once reading is complete
          reader.onload = () => {
            if(!reader.result) return;
            const base64String = (reader.result as string).split(',')[1];

            const dataUrl = `data:${file.type};base64,${base64String}`;
            setAdjustedCorners(null)
            setImageSrc(dataUrl);
            isUploadedFile.current = true
          };
      
          // Read the file as a Data URL
          reader.readAsDataURL(file);
        } else {
          console.error("No file selected");
        }
    }

    const handleSubmit = async () => {
      if(!imageSrc || !adjustedCorners) return;
      const cloneCorners = lodash.cloneDeep(adjustedCorners)

      const warpedBase = await warpDocumentFromBase64(imageSrc, cloneCorners, {w:screenshotCanvasRef.current?.width || 0, h:screenshotCanvasRef.current?.height || 0})
      if(!warpedBase){ toast.error('Document warping failed', {id:'handle-submit'}) }
      setImageSrc(null); setDocumentScanningStatus(null); setAdjustedCorners(null); isUploadedFile.current = false
      onSubmit&&onSubmit(warpedBase || '')
    }

  return (
    <>
    <Toaster />
    
    <AlertDialog open={open} onOpenChange={(o)=>{onChangeOpen(o)}}>
        <AlertDialogContent className="max-h-dvh overflow-auto h-dvh p-0 border-none bg-black max-w-[100dvw]">
            <div className="flex flex-col justify-between overflow-auto">               
                <div className="h-full flex flex-col overflow-auto relative">
                    {!imageSrc 
                    ? <AlertDialogCancel asChild className="border-none w-10 !p-0 h-10 hover:bg-transparent m-0"><button className="text-white absolute flex justify-center items-center h-10 w-10 top-3 left-3 z-10"><X className="text-white w-6 h-6"/></button></AlertDialogCancel>
                    : <button onClick={()=>{setImageSrc(null)}} className="h-10 w-10 flex justify-center items-center rounded-full bg-neutral-900/80 text-white backdrop-blur-md absolute top-3 left-3 z-10"><X className="w-6 h-6"/></button>
                    }

                    <div className="relative h-full w-full overflow-hidden rounded-b-3xl flex flex-col">
                        {imageSrc && 
                        <>
                        <img src={imageSrc} ref={imageRef} className="w-full h-full" alt="Captured" />
                        <canvas onTouchStart={handleMouseDown} onMouseDown={handleMouseDown} ref={screenshotCanvasRef} className="absolute top-0 left-0 w-full h-full bg-white/20" />
                        <p className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white bg-black px-2 py-1 whitespace-nowrap rounded-full text-sm">Verplaats de hoeken indien nodig</p>
                        </>
                        }
                        {!imageSrc &&
                        <>
                        <Webcam
                          className="absolute h-full w-full object-cover"
                          forceScreenshotSourceSize={true}
                          imageSmoothing={false}
                          audio={false}
                          screenshotQuality={1}
                          screenshotFormat="image/jpeg"
                          ref={webcamRef}                     
                          videoConstraints={{facingMode:isEnvironmentFacing?"environment":"user", frameRate:{ideal:60}}}
                          onUserMediaError={handleUserMediaError}
                        />
                        <canvas ref={webcamCanvasRef} className="absolute w-full h-full object-cover" />
                        {documentScanningStatus == 'loading' && <p className="absolute bottom-24 left-1/2 -translate-x-1/2 text-white bg-black px-2 py-1 whitespace-nowrap rounded-full text-sm">Stilhouden...</p>}
                        {documentScanningStatus == 'found' && <p className="absolute bottom-24 left-1/2 -translate-x-1/2 text-white bg-black px-2 py-1 whitespace-nowrap rounded-full text-sm">Document gescand</p>}
                        {(documentScanningStatus == 'none' || !documentScanningStatus) && <p className="absolute bottom-24 left-1/2 -translate-x-1/2 text-white bg-black px-2 py-1 whitespace-nowrap rounded-full text-sm">Geen document gevonden</p>}

                        </>
                        }
                    </div>

                    {!imageSrc && <button className="absolute bg-white bottom-6 left-1/2 -translate-x-1/2 w-12 h-12 rounded-full outline outline-4 outline-offset-2 outline-white" onClick={capture}></button>}
                </div>

                {!imageSrc 
                ? 
                <div className="flex items-center justify-between p-4">
                    <div className="w-10 h-10 flex items-center justify-start">
                        <label htmlFor="select-image"><div className="w-10 h-10 bg-neutral-700 text-white rounded-md flex justify-center items-center"><ImageIcon className="w-5 h-5"/></div></label>
                        <input type="file" accept="image/*" onChange={(e)=>{ handleUploadFile(e) }} hidden id="select-image"/>
                    </div>
                    <div className="flex items-center justify-center overflow-auto">
                        <p className="text-sm font-bold uppercase text-white">Scannen</p>
                    </div>
                    <div className="w-10 h-10 flex items-center justify-end">
                        <button onClick={()=>{setisEnvironmentFacing(!isEnvironmentFacing)}} className="w-10 h-10 bg-neutral-700 text-white rounded-full flex justify-center items-center"><RefreshCcwIcon className="w-5 h-5"/></button>
                    </div>
                </div>
                :
                <div className="flex justify-center items-center p-4">
                    {/* <button onClick={()=>{onScan && onScan(imageSrc); setImageSrc(null);}} className="w-10 h-10 rounded-full bg-white flex items-center justify-center"><ArrowRightIcon className="w-6 h-6" strokeWidth={2}/></button> */}
                    <button onClick={()=>{handleSubmit()}} className="w-10 h-10 rounded-full bg-white flex items-center justify-center"><ArrowRightIcon className="w-6 h-6" strokeWidth={2}/></button>

                </div>
                }
            </div>
        </AlertDialogContent>
    </AlertDialog>
    </>
  )
}

export default BarcodeScanner

const loadImage = (src:string) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = src;
    });
  };

const warpDocumentFromBase64 = async (base64Image:string, adjustedCorners:{x:number,y:number}[], canvasSize:{w:number,h:number}) => {     
    try {
      let img:any = await loadImage(base64Image); // Load the base64 image asynchronously

      const scale = 4

      // Create a canvas to resize the image
      const resizeCanvas = document.createElement('canvas');
      resizeCanvas.width = (canvasSize.w * scale);
      resizeCanvas.height = (canvasSize.h * scale);
      const resizeCtx = resizeCanvas.getContext('2d');
      if (!resizeCtx) return;

      // Draw the resized image on the canvas
      resizeCtx.drawImage(img, 0, 0, (canvasSize.w * scale), (canvasSize.h * scale));
      const abc = resizeCanvas.toDataURL('image/png');
      //base64Image = abc
      img = await loadImage(abc)

      adjustedCorners.forEach(c => {
        c.x = c.x * scale
        c.y = c.y * scale
      })
      
      // Create a canvas to draw the image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      if(!ctx) return;
      ctx.drawImage(img, 0, 0);
  
      // Get image data from the canvas
      const imageData = ctx.getImageData(0, 0, img.width, img.height);
      const srcMat = cv.matFromImageData(imageData);
  
      // Calculate the width and height for the destination rectangle
      const width = Math.max(
        distanceFunc(adjustedCorners[0], adjustedCorners[1]),
        distanceFunc(adjustedCorners[2], adjustedCorners[3])
      );
      const height = Math.max(
        distanceFunc(adjustedCorners[0], adjustedCorners[3]),
        distanceFunc(adjustedCorners[1], adjustedCorners[2])
      );

      // Adjust width and height to maintain a good resolution
      const scaleFactor = 1; // Increase to improve quality
      const dstWidth = width * scaleFactor;
      const dstHeight = height * scaleFactor;
  
      // Destination points for a rectangular output
      const dstQuad = cv.matFromArray(4, 1, cv.CV_32FC2, [
        0, 0,
        dstWidth - 1, 0,
        dstWidth - 1, dstHeight - 1,
        0, dstHeight - 1,
      ]);
  
      // Source points from adjustedCorners
      const srcQuad = cv.matFromArray(4, 1, cv.CV_32FC2, [
        adjustedCorners[0].x, adjustedCorners[0].y,
        adjustedCorners[1].x, adjustedCorners[1].y,
        adjustedCorners[2].x, adjustedCorners[2].y,
        adjustedCorners[3].x, adjustedCorners[3].y,
      ]);
  
      // Get the perspective transformation matrix
      const M = cv.getPerspectiveTransform(srcQuad, dstQuad);
  
      // Apply the perspective transformation
      const dstMat = new cv.Mat();
      const dsize = new cv.Size(dstWidth, dstHeight);
      cv.warpPerspective(srcMat, dstMat, M, dsize, cv.INTER_LANCZOS4, cv.BORDER_CONSTANT, new cv.Scalar());

      // Mirror the image horizontally
      const mirroredMat = new cv.Mat();
      cv.flip(dstMat, mirroredMat, 1); // Flip around y-axis for mirroring

      // Convert to grayscale
      const grayMat = new cv.Mat();
      cv.cvtColor(mirroredMat, grayMat, cv.COLOR_RGBA2GRAY);

      // Improve contrast using CLAHE
      const clahe = new cv.CLAHE(1.0, new cv.Size(8, 8)); 
      const enhanced = new cv.Mat();
      clahe.apply(grayMat, enhanced);

      // Add contrast adjustment
      const contrastFactor = 1.25; // Change this factor to adjust contrast (1.0 means no change)
      const imageDataGray = enhanced.data;
      for (let i = 0; i < imageDataGray.length; i++) {
        const pixel = imageDataGray[i];
        // Apply contrast adjustment formula
        imageDataGray[i] = Math.min(255, Math.max(0, (pixel - 128) * contrastFactor + 128));
      }
      enhanced.data.set(imageDataGray);
  
      // Convert the result to base64
      const canvasOutput = document.createElement('canvas');
      canvasOutput.width = enhanced.cols;
      canvasOutput.height = enhanced.rows;
      cv.imshow(canvasOutput, enhanced);
      const base64Result = canvasOutput.toDataURL('image/png');
  
      // Cleanup
      srcMat.delete();
      dstMat.delete();
      srcQuad.delete();
      dstQuad.delete();
      M.delete();
  
      return base64Result;
    } catch (error) {
      console.error('Error processing the image:', error);
      return null;
    }
};

const drawCornerCircles = (ctx:any, contour:any) => {
    ctx.fillStyle = "#FFFFFFb3";
    contour.forEach((point:any) => {
      ctx.beginPath();
      ctx.strokeStyle = "#2275A5";
      ctx.lineWidth = 3
      ctx.arc(point.x, point.y, 10, 0, 2 * Math.PI);
      
      ctx.stroke();
      ctx.fill();
    });
};

const drawFullContour = (ctx:any, contour:any) => {
    ctx.beginPath();
    ctx.strokeStyle = "#2275A5";
    ctx.lineWidth = 2;
    ctx.moveTo(contour[0].x, contour[0].y);
    for (let i = 1; i < 4; i++) {
      ctx.lineTo(contour[i].x, contour[i].y);
    }
    ctx.closePath();
    ctx.stroke();
    contour.forEach((point:any) => {
      ctx.fillStyle = "#2275A5";
      ctx.beginPath();
      ctx.arc(point.x, point.y, 5, 0, 2 * Math.PI);
      ctx.fill();
    });
};

const smoothContours = (previous:any, current:any) => {
    if (!previous || previous.length !== 4) {
      return current;
    }
    return current.map((point:any, index:number) => ({
      x: (point.x * 0.7 + previous[index].x * 0.3),
      y: (point.y * 0.7 + previous[index].y * 0.3),
    }));
};

const distanceFunc = (p1:any, p2:any) => {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
};

const detectDocumentCornersFromBase64 = (base64Image:string) => {
    return new Promise((resolve, reject) => {
      // Create an image element
      const img = new Image();
      if(base64Image.substring(0,4) === 'data'){
        img.src = base64Image
      } else {
        img.src = `data:image/jpeg;base64,${base64Image}`;
      }
      
      img.onload = () => {
        // Create a canvas and get its context
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if(!ctx) return;
  
        // Set the canvas size to the image size
        canvas.width = img.width;
        canvas.height = img.height;
  
        // Draw the image onto the canvas
        ctx.drawImage(img, 0, 0, img.width, img.height);
  
        // Get the image data from the canvas
        const imageData = ctx.getImageData(0, 0, img.width, img.height);
  
        // Convert the image data to an OpenCV matrix
        const src = cv.matFromImageData(imageData);
  
        // Convert to grayscale
        const gray = new cv.Mat();
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

        // Apply Gaussian blur
        const blurred = new cv.Mat();
        cv.GaussianBlur(gray, blurred, new cv.Size(9, 9), 0);

        // Apply adaptive thresholding
        const thresh = new cv.Mat();
        cv.adaptiveThreshold(blurred, thresh, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY_INV, 11, 2);

        // Use morphology to close gaps
        const kernel = cv.getStructuringElement(cv.MORPH_RECT, new cv.Size(5, 5));
        const morph = new cv.Mat();
        cv.morphologyEx(thresh, morph, cv.MORPH_CLOSE, kernel);

        // Detect edges using Canny
        const edges = new cv.Mat();
        cv.Canny(blurred, edges, 75, 200);

        // Find contours
        const contours = new cv.MatVector();
        const hierarchy = new cv.Mat();
        cv.findContours(edges, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

        const imgArea = img.width * img.height;
        let largestContour = null;
        let largestArea = 0;

        for (let i = 0; i < contours.size(); i++) {
          const contour = contours.get(i);
          const area = cv.contourArea(contour);

  
          if (area < imgArea * 0.1) continue; // Filter out small contours  
  
          const perimeter = cv.arcLength(contour, true);
          const approx = new cv.Mat();
          cv.approxPolyDP(contour, approx, 0.02 * perimeter, true);
  
          if (approx.rows === 4) {
              const points = [];
              for (let j = 0; j < 4; j++) {
                  points.push({
                      x: approx.data32S[j * 2],
                      y: approx.data32S[j * 2 + 1],
                  });
              }
  
              // Calculate aspect ratio
              const width = Math.max(
                  distanceFunc(points[0], points[1]),
                  distanceFunc(points[2], points[3])
              );
              const height = Math.max(
                  distanceFunc(points[0], points[3]),
                  distanceFunc(points[1], points[2])
              );
              const aspectRatio = width / height;
              if (aspectRatio < 0.5 || aspectRatio > 2.0) {
                  approx.delete();
                  continue; // Filter out contours that do not resemble a document
              }
  
              if (area > largestArea) {
                  largestArea = area;
                  largestContour = points;
              }
          }
          approx.delete();
        }

        console.log(largestContour)

        if(largestContour){
          resolve(largestContour);
        } else {
          const MARGIN_X = img.width/8
          const MARGIN_Y = img.height/6

          resolve([
            {"x": img.width-MARGIN_X, "y": MARGIN_Y},
            {"x": MARGIN_X, "y": MARGIN_Y},
            {"x": MARGIN_X, "y": img.height-MARGIN_Y},
            {"x": img.width-MARGIN_X, "y": img.height-MARGIN_Y}
        ])
        }
      };
  
      img.onerror = (error:any) => {
        reject("Image load error: " + error.message);
      };
    });
};

async function processMatToBase64(inputMat:any) {
  // Convert processed Mat to a canvas element
  const canvas = document.createElement('canvas');
  cv.imshow(canvas, inputMat);
  
  // Convert canvas content to Base64
  const base64Image = canvas.toDataURL('image/jpeg'); // Adjust format if needed (image/png, etc.)
  
  // Clean up memory
  inputMat.delete();
  
  return base64Image;
}